import React, { useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Lock, Loader2 } from 'lucide-react';

const formatError = (detail) => {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const AdminLogin = () => {
  const { login, isAuthenticated, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!loading && isAuthenticated) {
    return <Navigate to="/admin/blog" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/admin/blog');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#004D51] to-[#003338] px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center mb-8">
          <span className="text-3xl font-black text-white tracking-tight">VESTA <span className="text-[#F39C49]">WORKWEAR</span></span>
        </Link>
        <div className="bg-white rounded-2xl shadow-2xl p-8 border-4 border-[#F39C49]">
          <div className="flex items-center justify-center w-14 h-14 bg-[#004D51] rounded-full mx-auto mb-6">
            <Lock className="w-7 h-7 text-[#F39C49]" />
          </div>
          <h1 className="text-2xl font-black text-[#004D51] text-center mb-1">Admin Login</h1>
          <p className="text-sm text-[#545759] text-center mb-6">Sign in to manage your community blog.</p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-sm font-bold text-[#004D51] mb-1">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@vestaworkwear.com.au"
                data-testid="admin-email-input"
                className="h-12 border-2 border-[#004D51]/30 focus:border-[#004D51]"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#004D51] mb-1">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                data-testid="admin-password-input"
                className="h-12 border-2 border-[#004D51]/30 focus:border-[#004D51]"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 font-medium" data-testid="admin-login-error">{error}</p>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full h-12 bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black text-base rounded-lg disabled:opacity-70"
            >
              {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Signing in...</> : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
